import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import { createOrder } from '../services/ordersService';
import { processFlowPayment } from '../services/paymentsService';
import { AfiliacionesService } from '../services/afiliacionesServices';
import API_CONFIG from '../config/api';
import '../App.css';

// Función para leer el carrito desde localStorage (igual que CartPage)
function readCart() {
  try {
    const raw = localStorage.getItem('cart');
    const items = raw ? JSON.parse(raw) : [];

    return {
      items: items.map(item => ({
        producto_id: item.producto_id,
        producto: {
          id: item.producto_id,
          nombre: item.producto?.nombre || 'Producto',
          imagen_url: item.producto?.imagen_url || null,
          precio_actual: item.producto?.precio || 0,
          stock: 100
        },
        cantidad: item.cantidad,
        precio_unitario: item.producto?.precio || 0,
        subtotal: (item.producto?.precio || 0) * item.cantidad
      })),
      cantidad_items: items.reduce((s, i) => s + i.cantidad, 0),
      subtotal: items.reduce((s, i) => s + (i.producto?.precio || 0) * i.cantidad, 0)
    };
  } catch {
    return { items: [], cantidad_items: 0, subtotal: 0 };
  }
}

const CheckoutPage = () => {
  console.log('[Checkout] Componente CheckoutPage montado');
  
  const [cart, setCart] = useState({ items: [], subtotal: 0 });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const submittingRef = useRef(false);
  const [formData, setFormData] = useState({
    calle: '',
    numero: '',
    ciudad: '',
    region: '',
    codigo_postal: '',
    referencia: '',
    metodo_envio: 'estandar',
    observaciones: ''
  });
  const navigate = useNavigate();

  useEffect(() => {
    loadCart();
  }, []);

  useEffect(() => {
    const handleCartUpdate = () => {
      if (submittingRef.current) return;
      const cartData = readCart();
      setCart(cartData);
    };
    window.addEventListener('cart-updated', handleCartUpdate);
    window.addEventListener('storage', handleCartUpdate);
    return () => {
      window.removeEventListener('cart-updated', handleCartUpdate);
      window.removeEventListener('storage', handleCartUpdate);
    };
  }, []);

  const loadCart = () => {
    try {
      const cartData = readCart();
      
      if (cartData.items.length === 0) {
        alert('Tu carrito está vacío');
        navigate('/cart');
        return;
      }
      
      setCart(cartData);
    } catch (error) {
      console.error('Error al cargar carrito:', error);
      alert('Error al cargar el carrito. Por favor intenta nuevamente.');
      navigate('/cart');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('[Checkout] handleSubmit ejecutado - INICIO');
    
    // Verificar que el usuario tenga token antes de continuar
    const token = localStorage.getItem('access_token');
    if (!token) {
      console.error('[Checkout] Token faltante - redirigiendo a login');
      alert('Tu sesión ha expirado. Por favor inicia sesión nuevamente.');
      navigate('/login', { state: { from: '/checkout' } });
      return;
    }
    
    // Usar el carrito que se muestra en pantalla (cart state); si está vacío, intentar localStorage
    const sourceCart = (cart.items && cart.items.length > 0) ? cart : readCart();
    if (!sourceCart.items || sourceCart.items.length === 0) {
      alert('Tu carrito está vacío. Por favor agrega productos antes de continuar.');
      navigate('/cart');
      return;
    }
    
    // Separar afiliaciones de productos normales
    // Buscar afiliaciones por producto_id o por nombre del producto
    const afiliaciones = sourceCart.items.filter(item => {
      const productoId = String(item.producto_id || '').toLowerCase();
      const nombreProducto = String(item.producto?.nombre || '').toLowerCase();
      const isAfiliacion = productoId === 'afiliacion-achipo' || 
                          productoId.includes('afiliacion') ||
                          nombreProducto.includes('afiliación') ||
                          nombreProducto.includes('afiliacion');
      console.log('[Checkout] Item:', {
        producto_id: item.producto_id,
        nombre: item.producto?.nombre,
        isAfiliacion: isAfiliacion,
        productoIdLower: productoId,
        nombreLower: nombreProducto
      });
      return isAfiliacion;
    });
    const productosNormales = sourceCart.items.filter(item => {
      const productoId = String(item.producto_id || '').toLowerCase();
      const nombreProducto = String(item.producto?.nombre || '').toLowerCase();
      return productoId !== 'afiliacion-achipo' && 
             !productoId.includes('afiliacion') &&
             !nombreProducto.includes('afiliación') &&
             !nombreProducto.includes('afiliacion');
    });
    
    // Verificar si el carrito contiene solo afiliaciones (no requieren dirección de envío)
    const tieneSoloAfiliaciones = afiliaciones.length > 0 && productosNormales.length === 0;
    
    console.log('[Checkout] Análisis del carrito:', {
      totalItems: sourceCart.items.length,
      afiliaciones: afiliaciones.length,
      productosNormales: productosNormales.length,
      tieneSoloAfiliaciones,
      productoIds: sourceCart.items.map(item => item.producto_id)
    });
    
    // Si hay productos normales, validar dirección de envío
    if (!tieneSoloAfiliaciones && productosNormales.length > 0) {
      if (!formData.calle || !formData.ciudad || !formData.region || !formData.codigo_postal) {
        console.log('[Checkout] Validación fallida: campos incompletos');
        alert('Por favor completa todos los campos obligatorios de la dirección');
        return;
      }
    }

    setSubmitting(true);
    setPaymentError(null);
    submittingRef.current = true;

    try {
      // Si solo hay afiliaciones, procesarlas directamente sin crear pedido físico
      if (tieneSoloAfiliaciones) {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const email = user.email;

        if (!email) {
          console.error('[Checkout] No se encontró email del usuario:', user);
          throw new Error('No se encontró el email del usuario. Por favor inicia sesión nuevamente.');
        }

        // Obtener precio aplicable desde el backend (open/junior, con/sin club)
        let precioData;
        try {
          const res = await AfiliacionesService.getPrecioAfiliacion();
          precioData = res.precio_afiliacion || res;
        } catch (err) {
          console.error('[Checkout] Error al obtener precio de afiliación:', err);
          throw new Error(err.message || 'No se pudo obtener el precio de afiliación. Intenta de nuevo.');
        }
        const precioUnitario = precioData.precio ?? 0;
        const moneda = precioData.moneda || 'CLP';
        const cantidadAfiliaciones = afiliaciones.reduce((sum, item) => sum + (item.cantidad || 1), 0);
        const totalAfiliacion = precioUnitario * cantidadAfiliaciones;

        if (totalAfiliacion <= 0) {
          throw new Error('El monto de afiliación no es válido. Contacta al administrador.');
        }

        // Generar un ID único para la transacción de afiliación
        const afiliacionId = `AFI-${Date.now()}-${Math.random().toString(36).substr(2, 9).toUpperCase()}`;

        console.log('[Checkout] Procesando pago de afiliación directamente:', { afiliacionId, totalAfiliacion, moneda, tipo: precioData.tipo, email });

        // Limpiar carrito antes de procesar pago
        localStorage.removeItem('cart');
        window.dispatchEvent(new Event('cart-updated'));

        // Procesar pago directamente con Flow (usar precio y moneda del backend)
        let paymentResult;
        try {
          console.log('[Checkout] Llamando a processFlowPayment para afiliación...');
          paymentResult = await processFlowPayment({
            orderId: afiliacionId,
            amount: totalAfiliacion,
            currency: moneda,
            email,
            subject: `Afiliación ACHIPO - ${afiliaciones[0].producto?.nombre || 'Afiliación'}`,
            returnUrl: `${API_CONFIG.BASE_URL}/api/pagos/return?to=${encodeURIComponent('/pago-exitoso?tipo=afiliacion&id=' + afiliacionId)}`,
            cancelUrl: `${API_CONFIG.BASE_URL}/api/pagos/return?to=${encodeURIComponent('/pago-cancelado?tipo=afiliacion&id=' + afiliacionId)}`
          });
          console.log('[Checkout] Respuesta de processFlowPayment:', paymentResult);
        } catch (paymentError) {
          console.error('[Checkout] Error en processFlowPayment:', paymentError);
          throw new Error(`Error al procesar el pago: ${paymentError.message || 'Error desconocido'}`);
        }

        if (!paymentResult || !paymentResult.payment_url) {
          console.error('[Checkout] paymentResult inválido:', paymentResult);
          throw new Error('No se recibió la URL de pago de Flow. Por favor intenta nuevamente.');
        }
        
        console.log('[Checkout] Redirigiendo a Flow para afiliación:', paymentResult.payment_url);
        window.location.replace(paymentResult.payment_url);
        return;
      }
      
      // Verificar si hay productos de prueba (envío gratuito)
      // Detectar por nombre, ID, o cualquier campo que contenga "prueba" o "test"
      const tieneProductosPrueba = productosNormales.some(item => {
        const productoId = String(item.producto_id || '').toLowerCase();
        const nombreProducto = String(item.producto?.nombre || '').toLowerCase();
        const descripcion = String(item.producto?.descripcion || '').toLowerCase();
        const categoria = String(item.producto?.categoria || '').toLowerCase();
        
        // Buscar en múltiples campos
        return nombreProducto.includes('prueba') || 
               nombreProducto.includes('test') ||
               productoId.includes('prueba') ||
               productoId.includes('test') ||
               descripcion.includes('prueba') ||
               descripcion.includes('test') ||
               categoria.includes('prueba') ||
               categoria.includes('test');
      });
      
      // Si hay productos de prueba, el envío es 0, sino calcular según método
      const shippingCost = tieneProductosPrueba 
        ? 0 
        : (formData.metodo_envio === 'express' ? 5000 : formData.metodo_envio === 'estandar' ? 3000 : 0);
      
      const numericId = (id) => {
        const n = Number(id);
        return Number.isInteger(n) && n > 0 ? n : null;
      };
      const items = productosNormales
        .map(item => ({
          producto_id: numericId(item.producto_id),
          cantidad: Number(item.cantidad) || 1,
          precio_unitario: item.precio_unitario ?? item.producto?.precio_actual ?? item.producto?.precio ?? 0
        }))
        .filter(item => item.producto_id != null);

      // Si llegamos aquí y no hay items pero hay afiliaciones, algo salió mal
      // (debería haberse procesado arriba)
      if (items.length === 0) {
        if (afiliaciones.length > 0) {
          console.error('[Checkout] ERROR: Hay afiliaciones pero no se procesaron correctamente. Reintentando...');
          const user = JSON.parse(localStorage.getItem('user') || '{}');
          const email = user.email;

          if (email) {
            try {
              const res = await AfiliacionesService.getPrecioAfiliacion();
              const precioData = res.precio_afiliacion || res;
              const precioUnitario = precioData.precio ?? 0;
              const cantidadAfiliaciones = afiliaciones.reduce((sum, item) => sum + (item.cantidad || 1), 0);
              const totalAfiliacion = precioUnitario * cantidadAfiliaciones;

              if (totalAfiliacion > 0) {
                const afiliacionId = `AFI-${Date.now()}-${Math.random().toString(36).substr(2, 9).toUpperCase()}`;
                localStorage.removeItem('cart');
                window.dispatchEvent(new Event('cart-updated'));

                const paymentResult = await processFlowPayment({
                  orderId: afiliacionId,
                  amount: totalAfiliacion,
                  currency: precioData.moneda || 'CLP',
                  email,
                  subject: `Afiliación ACHIPO - ${afiliaciones[0].producto?.nombre || 'Afiliación'}`,
                  returnUrl: `${API_CONFIG.BASE_URL}/api/pagos/return?to=${encodeURIComponent('/pago-exitoso?tipo=afiliacion&id=' + afiliacionId)}`,
                  cancelUrl: `${API_CONFIG.BASE_URL}/api/pagos/return?to=${encodeURIComponent('/pago-cancelado?tipo=afiliacion&id=' + afiliacionId)}`
                });

                if (paymentResult && paymentResult.payment_url) {
                  window.location.replace(paymentResult.payment_url);
                  return;
                }
              }
            } catch (error) {
              console.error('[Checkout] Error al reintentar procesar afiliación:', error);
            }
          }
        }
        
        if (productosNormales.length > 0) {
          alert('No hay productos válidos de la tienda en tu carrito. Solo los productos de la tienda pueden enviarse por este formulario.');
        } else {
          alert('No hay productos válidos en tu carrito.');
        }
        setSubmitting(false);
        return;
      }

      const orderData = {
        items: items,
        direccion_entrega: {
          calle: formData.calle,
          numero: formData.numero,
          ciudad: formData.ciudad,
          region: formData.region,
          codigo_postal: formData.codigo_postal,
          referencia: formData.referencia
        },
        metodo_envio: formData.metodo_envio,
        subtotal: sourceCart.subtotal,
        envio: shippingCost,
        total: sourceCart.subtotal + shippingCost,
        observaciones: formData.observaciones
      };

      if (process.env.NODE_ENV !== 'production') {
        console.log('[Checkout] Enviando pedido con items:', orderData.items?.length, orderData.items);
      }

      console.log('[Checkout] Creando pedido...');
      const response = await createOrder(orderData);
      console.log('[Checkout] Respuesta de createOrder:', JSON.stringify(response, null, 2));

      if (!response || !response.success) {
        console.error('[Checkout] createOrder falló:', response);
        throw new Error(response?.error || 'Error al crear el pedido');
      }

      const pedido = response.data?.pedido;
      if (!pedido || !pedido.numero_pedido) {
        console.error('[Checkout] No se recibió pedido válido en la respuesta:', response);
        throw new Error('No se recibió información válida del pedido. Por favor intenta nuevamente.');
      }

      const numeroPedido = pedido.numero_pedido;
      const total = sourceCart.subtotal + shippingCost;

      console.log('[Checkout] ✅ Pedido creado exitosamente:', numeroPedido, 'Total:', total);
      console.log('[Checkout] ⚠️ IMPORTANTE: NO mostrar ningún alert aquí - continuar directamente con el pago');

      // Limpiar carrito tras crear pedido
      localStorage.removeItem('cart');
      window.dispatchEvent(new Event('cart-updated'));

      // Obtener email del usuario para Flow
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const email = user.email;

      if (!email) {
        console.error('[Checkout] No se encontró email del usuario:', user);
        throw new Error('No se encontró el email del usuario. Por favor inicia sesión nuevamente.');
      }

      console.log('[Checkout] Procesando pago con Flow:', { numeroPedido, total, email });

      // Procesar pago con Flow y redirigir - SIN mostrar ningún mensaje intermedio
      let paymentResult;
      try {
        console.log('[Checkout] Llamando a processFlowPayment...');
        paymentResult = await processFlowPayment({
          orderId: numeroPedido,
          amount: total,
          email,
          subject: `Pedido ${numeroPedido}`,
          returnUrl: `${API_CONFIG.BASE_URL}/api/pagos/return?to=${encodeURIComponent('/pago-exitoso?orden=' + numeroPedido)}`,
          cancelUrl: `${API_CONFIG.BASE_URL}/api/pagos/return?to=${encodeURIComponent('/pago-cancelado?orden=' + numeroPedido)}`
        });
        console.log('[Checkout] ✅ Respuesta de processFlowPayment:', JSON.stringify(paymentResult, null, 2));
      } catch (paymentError) {
        console.error('[Checkout] ❌ Error en processFlowPayment:', paymentError);
        console.error('[Checkout] Stack trace:', paymentError.stack);
        throw new Error(`Error al procesar el pago: ${paymentError.message || 'Error desconocido'}`);
      }

      // Validar que payment_url existe y es válida
      if (!paymentResult || !paymentResult.payment_url) {
        console.error('[Checkout] ❌ paymentResult inválido:', paymentResult);
        throw new Error('No se recibió la URL de pago de Flow. Por favor intenta nuevamente.');
      }

      // Validar que la URL es válida
      let paymentUrl;
      try {
        paymentUrl = new URL(paymentResult.payment_url);
        console.log('[Checkout] ✅ URL de pago válida:', paymentUrl.href);
      } catch (e) {
        console.error('[Checkout] ❌ URL de pago inválida:', paymentResult.payment_url, e);
        throw new Error('La URL de pago recibida no es válida. Por favor intenta nuevamente.');
      }

      // Redirigir a la URL de pago - IMPORTANTE: usar window.location.replace para evitar que el usuario pueda volver atrás
      console.log('[Checkout] 🚀 Redirigiendo a Flow:', paymentUrl.href);
      console.log('[Checkout] ⚠️ NO mostrar ningún alert antes de redirigir - redirigiendo inmediatamente');
      
      // Forzar redirección inmediata sin ningún delay
      window.location.replace(paymentUrl.href);
      
      // Si por alguna razón la redirección falla, intentar con href como fallback
      setTimeout(() => {
        if (window.location.href !== paymentUrl.href) {
          console.warn('[Checkout] ⚠️ window.location.replace no funcionó, intentando con href');
          window.location.href = paymentUrl.href;
        }
      }, 100);
    } catch (error) {
      console.error('Error al crear pedido o procesar pago:', error);
      
      const errorMessage = error.message || 'No se pudo procesar el pedido. Por favor intenta nuevamente.';
      setPaymentError(errorMessage);
      alert(`Error: ${errorMessage}`);
      
      // NO redirigir automáticamente - dejar al usuario en la página de checkout
    } finally {
      setSubmitting(false);
      submittingRef.current = false;
    }
  };

  // Verificar si el carrito contiene solo afiliaciones (no requieren envío físico)
  const tieneSoloAfiliaciones = cart.items.length > 0 && cart.items.every(item => {
    const productoId = String(item.producto_id || '').toLowerCase();
    const nombreProducto = String(item.producto?.nombre || '').toLowerCase();
    return productoId === 'afiliacion-achipo' || 
           productoId.includes('afiliacion') ||
           nombreProducto.includes('afiliación') ||
           nombreProducto.includes('afiliacion');
  });
  
  // Verificar si hay productos de prueba (envío gratuito)
  // Detectar por nombre, ID, o cualquier campo que contenga "prueba" o "test"
  const tieneProductosPrueba = cart.items.some(item => {
    const productoId = String(item.producto_id || '').toLowerCase();
    const nombreProducto = String(item.producto?.nombre || '').toLowerCase();
    const descripcion = String(item.producto?.descripcion || '').toLowerCase();
    const categoria = String(item.producto?.categoria || '').toLowerCase();
    
    // Log para debug (temporal)
    if (process.env.NODE_ENV !== 'production') {
      console.log('[Checkout] Verificando producto:', {
        productoId,
        nombreProducto,
        descripcion: descripcion.substring(0, 50),
        categoria,
        item: item
      });
    }
    
    // Buscar en múltiples campos
    const esPrueba = nombreProducto.includes('prueba') || 
           nombreProducto.includes('test') ||
           productoId.includes('prueba') ||
           productoId.includes('test') ||
           descripcion.includes('prueba') ||
           descripcion.includes('test') ||
           categoria.includes('prueba') ||
           categoria.includes('test');
    
    if (process.env.NODE_ENV !== 'production' && esPrueba) {
      console.log('[Checkout] Producto de prueba detectado:', nombreProducto);
    }
    
    return esPrueba;
  });
  
  // Si solo hay afiliaciones o hay productos de prueba, el envío es 0
  const shippingCost = (tieneSoloAfiliaciones || tieneProductosPrueba)
    ? 0 
    : (formData.metodo_envio === 'express' ? 5000 : formData.metodo_envio === 'estandar' ? 3000 : 0);
  const total = cart.subtotal + shippingCost;

  const defaultImg = `${process.env.PUBLIC_URL || ''}/contenido-no-disponible.jpeg`;

  if (loading) {
    return (
      <div className="main-container">
        <Header />
        <div className="cart-container">
          <div className="cart-header-minimal">
            <p style={{ margin: 0, color: '#6b7280', fontSize: '0.9rem' }}>Cargando...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="main-container">
      <Header />

      <div className="cart-container">
        <header className="cart-header-minimal">
          <Link to="/cart" className="cart-back">Carrito</Link>
          <h1 className="cart-title-minimal">
            Finalizar compra <span className="cart-title-count">({cart.cantidad_items ?? cart.items.length} {cart.items.length === 1 ? 'producto' : 'productos'})</span>
          </h1>
        </header>

        <form onSubmit={(e) => { e.preventDefault(); handleSubmit(e); }}>
          <div className="cart-layout">
            <section className="cart-list">
              <ul className="cart-list-items">
                {cart.items.map(item => (
                  <li key={item.producto_id} className="cart-row cart-row--readonly">
                    <div className="cart-row-image">
                      <img
                        src={item.producto?.imagen_url || defaultImg}
                        alt={item.producto?.nombre || 'Producto'}
                        onError={(e) => {
                          if (!e.target.src.includes('contenido-no-disponible.jpeg')) e.target.src = defaultImg;
                        }}
                      />
                    </div>
                    <div className="cart-row-info">
                      <h3 className="cart-row-name">{item.producto?.nombre || 'Producto'}</h3>
                      <p className="cart-row-unit">${(item.precio_unitario ?? 0).toLocaleString('es-CL')} c/u</p>
                    </div>
                    <div className="cart-row-qty">
                      <span className="cart-qty-value">x{item.cantidad}</span>
                    </div>
                    <div className="cart-row-total">${item.subtotal.toLocaleString('es-CL')}</div>
                  </li>
                ))}
              </ul>

              {!tieneSoloAfiliaciones && (
                <div className="cart-checkout-form">
                  <h2>Datos de envío</h2>
                  <label>Calle *</label>
                  <input type="text" name="calle" value={formData.calle} onChange={handleInputChange} required />

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div>
                      <label>Número</label>
                      <input type="text" name="numero" value={formData.numero} onChange={handleInputChange} />
                    </div>
                    <div>
                      <label>Ciudad *</label>
                      <input type="text" name="ciudad" value={formData.ciudad} onChange={handleInputChange} required />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div>
                      <label>Región *</label>
                      <input type="text" name="region" value={formData.region} onChange={handleInputChange} required />
                    </div>
                    <div>
                      <label>Código postal *</label>
                      <input type="text" name="codigo_postal" value={formData.codigo_postal} onChange={handleInputChange} required />
                    </div>
                  </div>

                  <label>Referencia (opcional)</label>
                  <input type="text" name="referencia" value={formData.referencia} onChange={handleInputChange} placeholder="Ej: Edificio azul, depto 405" />

                  <label>Método de envío *</label>
                  <select name="metodo_envio" value={formData.metodo_envio} onChange={handleInputChange} required>
                    <option value="estandar">Estándar ($3.000 - 5-7 días hábiles)</option>
                    <option value="express">Express ($5.000 - 2-3 días hábiles)</option>
                    <option value="retiro_tienda">Retiro en tienda (gratis)</option>
                  </select>

                  <label>Observaciones (opcional)</label>
                  <textarea name="observaciones" value={formData.observaciones} onChange={handleInputChange} rows={3} placeholder="Información adicional sobre tu pedido" />
                </div>
              )}

              {tieneSoloAfiliaciones && (
                <div className="cart-checkout-form">
                  <h2>Pago de afiliación</h2>
                  <p style={{ margin: 0, color: '#6b7280', fontSize: '0.9rem', lineHeight: 1.5 }}>
                    Las afiliaciones son digitales y no requieren datos de envío. Usa el botón de la derecha para proceder al pago.
                  </p>
                </div>
              )}

              <div className="cart-more">
                <Link to="/shop" className="cart-more-link">Seguir comprando</Link>
              </div>
            </section>

            <aside className="cart-summary">
              <div className="cart-summary-inner">
                {paymentError && (
                  <div className="cart-payment-error">
                    <strong>Error al procesar el pago:</strong>
                    <pre style={{ margin: '8px 0 0', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'inherit', fontSize: '0.85rem' }}>{paymentError}</pre>
                  </div>
                )}
                <table className="cart-summary-table">
                  <tbody>
                    <tr>
                      <td>Subtotal</td>
                      <td>${cart.subtotal.toLocaleString('es-CL')}</td>
                    </tr>
                    <tr>
                      <td>Envío</td>
                      <td>${shippingCost.toLocaleString('es-CL')}</td>
                    </tr>
                    <tr className="cart-summary-total">
                      <td>Total</td>
                      <td>${total.toLocaleString('es-CL')}</td>
                    </tr>
                  </tbody>
                </table>

                <button type="submit" disabled={submitting} className="cart-checkout-btn">
                  {submitting ? 'Procesando...' : tieneSoloAfiliaciones ? 'Proceder al pago' : 'Confirmar pedido'}
                </button>

                <p className="cart-secure">Pago seguro</p>
              </div>
            </aside>
          </div>
        </form>
      </div>

      <Footer />
    </div>
  );
};

export default CheckoutPage;